import React, { useRef } from 'react';
import type { FeatureItem, FeaturesData } from 'types/websiteBuilder';
import { compressImageFile } from 'utils/imageUtils';
import galleryPlaceholder from 'assets/websiteBuilderDefaults/gallery-placeholder.svg';
import featuresDefaultComposite from 'assets/websiteBuilderDefaults/features-default.png';
import styles from './FeaturesSection.module.scss';

const DEFAULT_TITLE_PLACEHOLDER = 'Card title';
const DEFAULT_LINK_PLACEHOLDER = '/';

function createDefaultItem(index: number): FeatureItem {
  const defaults = [
    'Community Outreach',
    'Youth Bible Camp',
    'Mentorship Program',
  ];
  return {
    imageUrl: featuresDefaultComposite,
    title: defaults[index] ?? 'Community Work',
    link: '/',
  };
}

function getCompositePos(index: number): string {
  if (index === 0) return '0% 50%';
  if (index === 1) return '50% 50%';
  return '100% 50%';
}

function isCompositeImage(url: string | undefined): boolean {
  const trimmed = (url ?? '').trim();
  return trimmed === '' || trimmed === featuresDefaultComposite;
}

function ensureThreeItems(items: FeatureItem[]): FeatureItem[] {
  const normalized = [...items];
  while (normalized.length < 3)
    normalized.push(createDefaultItem(normalized.length));
  return normalized.slice(0, 3);
}

interface FeaturesSectionEditorProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function getData(data: Record<string, unknown>): FeaturesData {
  const parsedItems = Array.isArray(data.items)
    ? (data.items as Record<string, unknown>[]).map((i) => ({
        imageUrl:
          typeof i.imageUrl === 'string'
            ? i.imageUrl
            : typeof i.icon === 'string'
              ? i.icon
              : '',
        title: typeof i.title === 'string' ? i.title : '',
        link: typeof i.link === 'string' ? i.link : '/',
      }))
    : [];

  const items = ensureThreeItems(parsedItems);

  return {
    title:
      typeof data.title === 'string' ? data.title : 'Our Work in the Community',
    subtitle:
      typeof data.subtitle === 'string'
        ? data.subtitle
        : 'Let your work speak for itself by presenting a portfolio. Share your previous projects here so clients can see your actual output.',
    items,
  };
}

export function FeaturesSectionEditor({
  data,
  onUpdateData,
}: FeaturesSectionEditorProps) {
  const d = getData(data);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingIconIndexRef = useRef<number | null>(null);

  const updateItem = (
    index: number,
    field: keyof FeatureItem,
    value: string,
  ) => {
    const next = [...(d.items ?? [])];
    if (!next[index]) next[index] = createDefaultItem(index);
    next[index] = { ...next[index], [field]: value };
    onUpdateData({ ...data, items: ensureThreeItems(next) });
  };

  const handlePickIcon = (index: number) => {
    pendingIconIndexRef.current = index;
    fileInputRef.current?.click();
  };

  const handleIconFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const index = pendingIconIndexRef.current;
    if (!file || index == null) return;
    // Use JPEG compression to keep payload small enough for localStorage.
    compressImageFile(file, { maxWidth: 640, maxHeight: 640, quality: 0.72 })
      .then((imageUrl) => updateItem(index, 'imageUrl', imageUrl))
      .catch(() => {});
    e.target.value = '';
    pendingIconIndexRef.current = null;
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className={styles.fileInput}
        onChange={handleIconFileChange}
        aria-label="Upload feature icon"
      />
      <input
        type="text"
        className={styles.titleInput}
        value={d.title}
        onChange={(e) => onUpdateData({ ...data, title: e.target.value })}
        placeholder="Section title"
      />
      <textarea
        className={styles.subtitleInput}
        value={d.subtitle ?? ''}
        onChange={(e) => onUpdateData({ ...data, subtitle: e.target.value })}
        placeholder="Section subtitle"
        rows={2}
      />
      <div className={styles.grid}>
        {(d.items ?? []).map((item, i) => (
          <div key={i} className={styles.card}>
            <div className={styles.iconRow}>
              {isCompositeImage(item.imageUrl) ? (
                <div
                  className={styles.cardImagePreviewComposite}
                  aria-hidden="true"
                  style={{
                    backgroundImage: `url(${featuresDefaultComposite})`,
                    ['--pos' as never]: getCompositePos(i),
                  }}
                />
              ) : (
                <img
                  className={styles.cardImagePreview}
                  src={
                    item.imageUrl?.trim() ? item.imageUrl : galleryPlaceholder
                  }
                  alt=""
                  aria-hidden="true"
                />
              )}
              <button
                type="button"
                className={styles.iconBrowseBtn}
                onClick={() => handlePickIcon(i)}
              >
                Browse
              </button>
              <button
                type="button"
                className={styles.iconBrowseBtn}
                onClick={() => updateItem(i, 'imageUrl', '')}
                disabled={!item.imageUrl?.trim()}
              >
                Clear
              </button>
            </div>
            <input
              type="text"
              className={styles.cardTitleInput}
              value={item.title}
              onChange={(e) => updateItem(i, 'title', e.target.value)}
              placeholder={DEFAULT_TITLE_PLACEHOLDER}
            />
            <input
              type="text"
              className={styles.cardLinkInput}
              value={item.link ?? ''}
              onChange={(e) => updateItem(i, 'link', e.target.value)}
              placeholder={DEFAULT_LINK_PLACEHOLDER}
            />
          </div>
        ))}
      </div>
    </>
  );
}

interface FeaturesSectionProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function FeaturesSection({ data }: FeaturesSectionProps) {
  const d = getData(data);

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <h2 className={styles.title}>{d.title}</h2>
        <p className={styles.subtitle}>{d.subtitle}</p>
        <div className={styles.grid}>
          {(d.items ?? []).map((item, i) => (
            <a
              key={i}
              href={item.link?.trim() || '#'}
              className={styles.card}
              onClick={(e) => {
                if (!item.link?.trim()) e.preventDefault();
              }}
            >
              {isCompositeImage(item.imageUrl) ? (
                <div
                  className={styles.cardImageComposite}
                  role="img"
                  aria-label={item.title?.trim() || 'Feature'}
                  style={{
                    backgroundImage: `url(${featuresDefaultComposite})`,
                    ['--pos' as never]: getCompositePos(i),
                  }}
                />
              ) : (
                <img
                  className={styles.cardImage}
                  src={
                    item.imageUrl?.trim() ? item.imageUrl : galleryPlaceholder
                  }
                  alt={item.title?.trim() || 'Feature'}
                />
              )}
              <h3 className={styles.cardTitle}>
                {item.title?.trim() ? item.title : DEFAULT_TITLE_PLACEHOLDER}
              </h3>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export default FeaturesSection;
